
import { useState, useEffect } from 'react';

export interface CartItem {
  id: string;
  name: string;
  size: string;
  price: number;
  originalPrice: number;
  image: string;
  quantity: number;
}

const CART_KEY = 'fpn_cart';

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

// Global listeners so all components stay in sync
const listeners = new Set<() => void>();

function notifyAll() {
  listeners.forEach((fn) => fn());
}

export function addToCart(item: Omit<CartItem, 'quantity'>, qty: number) {
  const cart = loadCart();
  const existing = cart.find((c) => c.id === item.id);
  if (existing) {
    existing.quantity += qty;
  } else {
    cart.push({ ...item, quantity: qty });
  }
  saveCart(cart);
  notifyAll();
}

export function updateCartItemQty(id: string, quantity: number) {
  const cart = loadCart();
  const item = cart.find((c) => c.id === id);
  if (item) {
    item.quantity = Math.max(1, quantity);
    saveCart(cart);
    notifyAll();
  }
}

export function removeCartItem(id: string) {
  const cart = loadCart().filter((c) => c.id !== id);
  saveCart(cart);
  notifyAll();
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    const refresh = () => setItems(loadCart());
    listeners.add(refresh);
    return () => { listeners.delete(refresh); };
  }, []);

  const totalCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return { items, totalCount, subtotal };
}
